// ─── YouTube embeds ───────────────────────────────────────────────────────────
// Why the player is built this way: YouTube no longer lets a page pick the
// quality. setPlaybackQuality() and the old vq= URL parameter are both ignored
// (measured: a request for 1080p on a 578px player stays at 720p). What it does
// honour is the size the player is *laid out* at — 320px gets 360p, 578px gets
// 720p, 1280px gets 1080p — and that holds when the iframe is then scaled down
// with a CSS transform. So the embed is laid out at 1080p and shrunk to fit.
// Scaling would make YouTube's own controls unusably small, so they are turned
// off and the page draws its own (see ControlBar).
//
// Shorts are laid out wide too. In a tall player YouTube switches to its Shorts
// interface — logo, Like / Share, caption, prev / next buttons — drawn over the
// picture. In a wide one the 9:16 video is just pillarboxed, so it is scaled up
// until that middle column fills the box and the stage crops the black sides.
// ─────────────────────────────────────────────────────────────────────────────

const RENDER = { w: 1920, h: 1080 };
const SHORT_COLUMN = (RENDER.h * 9) / 16;   // width of a 9:16 picture inside the wide render

/** Size every embed is laid out at, so YouTube serves 1080p. */
export function ytRenderSize() {
  return RENDER;
}

/**
 * Scale that fits the picture into a boxW x boxH stage. A wide film fits the
 * whole render; a vertical one fits its pillarboxed middle column (the rest of
 * the render hangs outside the stage and is cropped).
 */
export function ytFitScale(boxW, boxH, vertical) {
  if (!boxW || !boxH) return 0;
  return vertical
    ? Math.min(boxH / RENDER.h, boxW / SHORT_COLUMN)
    : Math.min(boxW / RENDER.w, boxH / RENDER.h);
}

/**
 * clip-path for a vertical film's embed: only its picture column stays visible.
 * Anything YouTube draws outside it (a Short's caption sits at the render's far
 * left) is cropped by the card anyway, but fullscreen is wide enough to show it.
 */
export function ytClip(vertical) {
  if (!vertical) return undefined;
  const side = (RENDER.w - SHORT_COLUMN) / 2;
  return `inset(0 ${side}px)`;
}

/** Embed URL for a page-drawn player: no chrome, loops, JS API on. */
export function ytEmbedUrl(id, { origin } = {}) {
  const q = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
    loop: '1',
    playlist: id,       // loop only works for a playlist, even of one
    enablejsapi: '1',
  });
  if (origin) q.set('origin', origin);
  return `https://www.youtube-nocookie.com/embed/${id}?${q}`;
}

const YT_ORIGIN = 'https://www.youtube-nocookie.com';

/**
 * Talks to an embed through its postMessage interface — what YouTube's own
 * IFrame API script does, minus the script. Commands (playVideo, seekTo, mute…)
 * go in; `onInfo` gets the player's reports (currentTime, duration, playerState).
 * playerState: -1 unstarted · 0 ended · 1 playing · 2 paused · 3 buffering · 5 cued
 */
export function createYouTubeBridge(frame, { onInfo, onReady } = {}) {
  let alive = true;
  const post = (msg) => {
    try {
      frame.contentWindow?.postMessage(JSON.stringify({ ...msg, id: 1, channel: 'widget' }), YT_ORIGIN);
    } catch { /* the frame went away */ }
  };

  const onMessage = (e) => {
    if (!alive || e.source !== frame.contentWindow) return;
    let d = e.data;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return; } }
    if (!d || typeof d !== 'object') return;
    if (d.event === 'onReady') onReady?.();
    if (d.info && (d.event === 'infoDelivery' || d.event === 'initialDelivery')) onInfo?.(d.info);
  };
  window.addEventListener('message', onMessage);

  // The player only starts reporting once it has heard it is being listened to,
  // and it may not have booted yet — so keep asking until it answers.
  post({ event: 'listening' });
  const handshake = setInterval(() => post({ event: 'listening' }), 500);

  return {
    command: (func, args = []) => post({ event: 'command', func, args }),
    destroy() {
      alive = false;
      clearInterval(handshake);
      window.removeEventListener('message', onMessage);
    },
  };
}
